<script lang="ts">
  import { t } from '../../lib/i18n/index.svelte.js'
  import type { ListStore } from '../../lib/state/listStore.svelte.js'
  import EmojiPicker from './EmojiPicker.svelte'
  import ProgressBar from './ProgressBar.svelte'

  interface Props {
    store: ListStore
    autofocusTitle: boolean
  }
  const { store, autofocusTitle }: Props = $props()

  let pickerOpen = $state(false)
  let editingDescription = $state(false)
  let titleField = $state<HTMLTextAreaElement | null>(null)
  let descriptionDraft = $state('')

  const list = $derived(store.list)
  const canEdit = $derived(store.canEdit)

  function grow(node: HTMLTextAreaElement | null): void {
    if (!node) return
    node.style.height = 'auto'
    node.style.height = `${node.scrollHeight}px`
  }

  $effect(() => {
    grow(titleField)
    if (autofocusTitle && titleField) titleField.focus()
  })

  function commitTitle(event: Event): void {
    const value = (event.currentTarget as HTMLTextAreaElement).value.trim()
    if (!value || value === list?.title) return
    store.updateList({ title: value })
  }

  function startDescription(): void {
    descriptionDraft = list?.description ?? ''
    editingDescription = true
  }

  function commitDescription(): void {
    editingDescription = false
    const value = descriptionDraft.trim()
    if (value !== list?.description) store.updateList({ description: value })
  }
</script>

{#if list}
  <header class="head">
    <div class="top">
      <button
        class="emoji"
        onclick={() => canEdit && (pickerOpen = true)}
        disabled={!canEdit}
        aria-label="Emoji"
      >
        {#if list.emoji}{list.emoji}{:else}<span class="placeholder"></span>{/if}
      </button>

      <textarea
        bind:this={titleField}
        class="title"
        rows="1"
        readonly={!canEdit}
        value={list.title}
        placeholder={t('list_title_placeholder')}
        aria-label={t('list_title_placeholder')}
        maxlength="200"
        oninput={(e) => grow(e.currentTarget)}
        onblur={commitTitle}
        onkeydown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
      ></textarea>
    </div>

    {#if editingDescription}
      <textarea
        class="description editing"
        bind:value={descriptionDraft}
        rows="2"
        maxlength="2000"
        placeholder={t('description_placeholder')}
        aria-label={t('description_placeholder')}
        oninput={(e) => grow(e.currentTarget)}
        onblur={commitDescription}
        onkeydown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            editingDescription = false
          }
        }}
      ></textarea>
    {:else if list.description}
      <button class="description" onclick={() => canEdit && startDescription()} disabled={!canEdit}>
        {list.description}
      </button>
    {:else if canEdit}
      <button class="add-description" onclick={startDescription}>{t('add_description')}</button>
    {/if}

    <ProgressBar checked={store.stats.checked} total={store.stats.total} />
  </header>

  <EmojiPicker
    open={pickerOpen}
    current={list.emoji}
    onclose={() => (pickerOpen = false)}
    onpick={(emoji) => {
      pickerOpen = false
      store.updateList({ emoji })
    }}
  />
{/if}

<style>
  .head {
    padding-top: var(--space-4);
  }
  .top {
    display: flex;
    align-items: flex-start;
    gap: var(--space-2);
  }
  .emoji {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    border: none;
    border-radius: var(--radius);
    background: transparent;
    font-size: 28px;
    line-height: 1;
  }
  .emoji:hover:not(:disabled) {
    background: var(--surface-2);
  }
  .placeholder {
    width: 26px;
    height: 26px;
    border: 2px dashed var(--border-strong);
    border-radius: var(--radius-sm);
  }
  .title {
    flex: 1;
    min-width: 0;
    padding: var(--space-1) 0;
    border: none;
    background: transparent;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.2;
    letter-spacing: -0.01em;
    resize: none;
    overflow: hidden;
  }
  .title:focus {
    outline: none;
  }
  .title::placeholder {
    color: var(--fg-faint);
  }
  .description,
  .add-description {
    display: block;
    width: 100%;
    padding: var(--space-1) 0 0 52px;
    border: none;
    background: transparent;
    text-align: left;
    font-size: 15px;
    color: var(--fg-muted);
    overflow-wrap: anywhere;
  }
  .add-description {
    color: var(--fg-faint);
  }
  .description.editing {
    padding-left: 52px;
    resize: none;
    overflow: hidden;
    font-family: inherit;
  }
</style>
